import { useI18n, type MessageKey } from '@ecom/i18n';
import { Badge, EmptyState, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { useAlertsQuery, type AlertSeverity } from '../api';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

const TONE: Record<AlertSeverity, BadgeTone> = {
  critical: 'critical',
  warning: 'warn',
  info: 'info',
};
const SEVERITY_KEY: Record<AlertSeverity, MessageKey> = {
  critical: 'admin.security.severity.critical',
  warning: 'admin.security.severity.warning',
  info: 'admin.security.severity.info',
};

const KNOWN = new Set([
  'login_failures_spike',
  'login_failures_single_ip',
  'accounts_locked',
  'break_glass_executed',
  'last_superadmin_override',
  'superadmin_promoted',
  'platform_role_revoked',
  'platform_account_disabled',
  'platform_admin_created',
  'owner_password_reissued',
  'admin_password_reissued',
  'promotion_pending',
  'superadmin_new_address',
  'no_second_superadmin',
]);

function alertKey(code: string): MessageKey {
  return KNOWN.has(code)
    ? (`admin.security.alert.${code}` as MessageKey)
    : 'admin.security.alert.unknown';
}

/**
 * The alerts, COMPUTED by the database each time this is opened from the journal and the sessions: nothing is stored and nothing is
 * sent (there is no e-mail). Only the rules that fire are listed, critical first. The sentence is the screen's own (translated); the
 * server sends a code, a count and a few non-sensitive facts.
 */
export function AlertsPanel({ reason }: { readonly reason: string }): ReactElement {
  const { t } = useI18n();
  const query = useAlertsQuery(reason, true);

  if (query.isPending) return <TableSkeleton columns={1} />;
  if (query.isError) return <EmptyState title={t('admin.security.loadError')} />;

  const alerts = query.data.alerts;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-inkFaint">{t('admin.security.alerts.note')}</p>
      {alerts.length === 0 ? (
        <EmptyState title={t('admin.security.alerts.empty')} />
      ) : (
        <ul className="flex flex-col gap-2">
          {alerts.map((alert) => (
            <li
              key={alert.code}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface p-3"
            >
              <Badge tone={TONE[alert.severity]}>{t(SEVERITY_KEY[alert.severity])}</Badge>
              <span className="text-sm text-ink">
                {t(alertKey(alert.code), { count: alert.count })}
              </span>
              {typeof alert.detail['ip'] === 'string' ? (
                <span className="font-mono text-xs text-inkFaint">{alert.detail['ip']}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
